import {useSelector} from 'react-redux';
import {useCallback} from 'react';
import {actions as bulkEditActions} from '../duck';
import {useActions} from '../../../utils/globalStore';
import {Product, ProductDetailVariant} from '../../../entities';

import type {BulkEditOption} from '../duck';
import type {RootState} from '../..';
import {bulkDeleteProductService, bulkEditProductService} from '../promises';

const bulkEditOptionSelector = (state: RootState) =>
  state.bulkEdit.bulkEditOption;
const setBulkEditOptionActions = bulkEditActions.setBulkEditOption;

export function useBulkEditOption() {
  const option = useSelector(bulkEditOptionSelector);
  const setBulkEditOption = useActions(setBulkEditOptionActions);

  const setOptionType = (type: BulkEditOption) => {
    if (option?.type === type) {
      return setBulkEditOption({type: undefined, value: undefined});
    }
    setBulkEditOption({type, value: ''});
  };

  const setOptionValue = (value: string) => {
    if (!option) return;
    setBulkEditOption({type: option.type, value});
  };

  return {
    option,
    setOptionType,
    setOptionValue,
  };
}

export function useToggleProductSelection() {
  const toggleProductSelectionAction = useActions(
    bulkEditActions.toggleProductSelection
  );

  const toggleProductSelection = (product: Product) => {
    toggleProductSelectionAction(product);
  };

  return toggleProductSelection;
}

export function useToggleVariantSelection() {
  const toggleVariantSelectionAction = useActions(
    bulkEditActions.toggleVariantSelection
  );

  const toggleVariantSelection = (variant: ProductDetailVariant, productId) => {
    toggleVariantSelectionAction(variant, productId);
  };

  return toggleVariantSelection;
}

export function useToggleVariantsInProductSelection() {
  const toggleVariantsInProductSelectionAction = useActions(
    bulkEditActions.toggleVariantsInProduct
  );

  const toggleVariantsInProductSelection = (
    product: Product,
    limitedSelected: number
  ) => {
    toggleVariantsInProductSelectionAction(product, limitedSelected);
  };

  return toggleVariantsInProductSelection;
}

export function useDeleteToggleProductSelection() {
  const deleteToggleProductSelectionAction = useActions(
    bulkEditActions.removeAllToggleProductSelection
  );

  const deleteToggleProductSelection = () => {
    deleteToggleProductSelectionAction();
  };

  return deleteToggleProductSelection;
}

const selectedProductsSelector = (state: RootState) =>
  state.bulkEdit.selectedProducts;

export function useSelectedProducts() {
  const productSelection = useSelector(selectedProductsSelector);

  return productSelection;
}

const selectedProductsVariantsSelector = (state: RootState) =>
  state.bulkEdit.selectedProductsVariants;

export function useSelectedProductsVariants() {
  const selectedVariants = useSelector(selectedProductsVariantsSelector);

  return selectedVariants;
}

const bulkEditFeatureSpec = (state: RootState) =>
  state.store.store.features.find(
    feature => feature.name === 'PRODUCTS_BULK_EDIT'
  );

export function useFeatureSpecLimit() {
  const featureSpec = useSelector(bulkEditFeatureSpec);

  if (!featureSpec || !featureSpec.spec) {
    return 0;
  }

  const featureSpecLimit = featureSpec.spec.limit;
  return featureSpecLimit;
}

export function useSelectedProductActions() {
  const selectedProducts = useSelectedProducts();

  const deleteProducts = useActions(bulkEditActions.deleteSelectedProducts);
  const editProducts = useActions(bulkEditActions.editSelectedProducts);

  const editSelectedProducts = (data: any) => {
    return bulkEditProductService(data).then(() => {
      editProducts(data);
    });
  };

  const deleteSelectedProducts = () => {
    const selectedProductsID = selectedProducts.map(product => product.id);
    return bulkDeleteProductService(selectedProductsID).then(() => {
      deleteProducts();
    });
  };

  return {
    editSelectedProducts,
    deleteSelectedProducts,
  };
}

export function useUpdateProductSelected() {
  const updatedProductSelectedAction = useActions(
    bulkEditActions.updateProductSelected
  );

  const updateProductSelected = useCallback(
    product => {
      updatedProductSelectedAction(product);
    },
    [updatedProductSelectedAction]
  );

  return {updateProductSelected};
}
